import React, { useState, useEffect } from 'react'
import {
  fetchImages,
  deleteImage,
  updateImageStatus,
} from '../../services/gallery'
import UploadZone from './Gallery/UploadZone'
import GalleryContainer from './Gallery/GalleryContainer'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import MyButton from './buttons'
import GalleryAlbums from './Gallery/GalleryAlbum'
import {
  Image,
  AlbumType,
  addToAlbum,
  removeFromAlbum,
  isImageInAlbum,
} from './Gallery/types/models'
import GalleryUsageInfo from './Gallery/GalleryUsageInfo'
import GalleryActionButtons from './Gallery/GalleryActionButtons'
import AlbumSelect from './Gallery/AlbumSelect'
import { FaInfoCircle } from 'react-icons/fa'
import StorageFullModal from './Gallery/StorageFullModal'
import { Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import CookieManager from '../lib/CookiesManager'

const calculateTotalSize = (images: Image[]): number => {
  return images.reduce((acc, img) => acc + img.size, 0)
}

const GalleryMegaEdit: React.FC = () => {
  const [images, setImages] = useState<Image[]>([])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const { t } = useTranslation()
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumType>('announce')
  const userId = CookieManager.getUserId() || ''
  const token = CookieManager.getToken() || ''
  const [mainGallerySelection, setMainGallerySelection] = useState<string[]>([])
  const [albumGallerySelection, setAlbumGallerySelection] = useState<string[]>(
    [],
  )
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteProgress, setDeleteProgress] = useState(0)
  const [showStorageFullModal, setShowStorageFullModal] = useState(false)
  const totalSize = calculateTotalSize(images)
  const maxSize = 10
  const pathname = window.location.pathname
  const isGalleryPage = pathname === '/dashboard/my-gallery/'

  const checkStorageAvailability = (newFileSize: number): boolean => {
    if (totalSize + newFileSize > maxSize) {
      setShowStorageFullModal(true)
      return false
    }
    return true
  }

  useEffect(() => {
    loadImages()
  }, [])

  useEffect(() => {
    if (!isGalleryPage) {
      switch (pathname) {
        case '/dashboard/announce-work/':
          setSelectedAlbum('jobOffer')
          break
        case '/dashboard/announce-event/':
          setSelectedAlbum('event')
          break
        case '/dashboard/announce/':
          setSelectedAlbum('announce')
          break
      }
    }
  }, [pathname, isGalleryPage])

  const handleAlbumChange = (newAlbumType: AlbumType) => {
    if (isGalleryPage) {
      setSelectedAlbum(newAlbumType)
      setMainGallerySelection([])
      setAlbumGallerySelection([])
    }
  }

  const loadImages = async () => {
    try {
      const fetchedImages = await fetchImages(userId, token)
      setImages(fetchedImages)
    } catch (error) {
      console.error('Failed to load images:', error)
      toast.error(t('failed-to-load-images'))
    }
  }

  const handleMainGallerySelection = (fileName: string) => {
    setMainGallerySelection(prev =>
      prev.includes(fileName)
        ? prev.filter(name => name !== fileName)
        : [...prev, fileName],
    )
    setAlbumGallerySelection([])
  }

  const handleAlbumGallerySelection = (fileName: string) => {
    setAlbumGallerySelection(prev =>
      prev.includes(fileName)
        ? prev.filter(name => name !== fileName)
        : [...prev, fileName],
    )
    setMainGallerySelection([])
  }

  const handleImagesUploaded = () => {
    loadImages()
  }

  const cleanFileName = (fileName: string): string => {
    return fileName.split('-').slice(2).join('-')
  }

  const handleAddToAlbum = async (fileNames: string[]) => {
    try {
      await Promise.all(
        fileNames.map(async fileName => {
          const image = images.find(img => img.fileName === fileName)
          if (!image) return

          const newStatus = addToAlbum(image.status, selectedAlbum)
          await handleUpdateStatus(fileName, newStatus)
        }),
      )

      toast.success(t('images-added-to-album'))
      setMainGallerySelection([])
      await loadImages()
    } catch (error) {
      console.error('Failed to add images to album:', error)
      toast.error(t('failed-to-add-images-to-album'))
    }
  }

  const handleSelectAll = (gallery: 'main' | 'album') => {
    if (gallery === 'main') {
      const mainGalleryImages = images.filter(
        image => !isImageInAlbum(image.status, selectedAlbum),
      )
      const allFileNames = mainGalleryImages.map(img => img.fileName)
      setMainGallerySelection(allFileNames)
      setAlbumGallerySelection([])
    } else {
      const albumImages = images.filter(image =>
        isImageInAlbum(image.status, selectedAlbum),
      )
      const allFileNames = albumImages.map(img => img.fileName)
      setAlbumGallerySelection(allFileNames)
      setMainGallerySelection([])
    }
  }

  const handleUpdateStatus = async (fileName: string, newStatus: number) => {
    try {
      await updateImageStatus(userId, token, fileName, newStatus)
      await loadImages()
    } catch (error) {
      console.error('Failed to update status:', error)
      toast.error(t('failed-to-update-status'))
    }
  }

  const handleDeleteSelected = async () => {
    try {
      setIsDeleting(true)
      const totalImages = mainGallerySelection.length

      for (let i = 0; i < mainGallerySelection.length; i++) {
        const fileName = mainGallerySelection[i]
        await deleteImage(userId, token, fileName)

        const progress = ((i + 1) / totalImages) * 100
        setDeleteProgress(progress)
      }

      await loadImages()
      setMainGallerySelection([])
      setIsDeleting(false)
      setDeleteProgress(0)
      setIsDeleteModalOpen(false)
      toast.success(t('selected-images-deleted'))
    } catch (error) {
      console.error('Failed to delete images:', error)
      toast.error(t('failed-to-delete-images'))
      setIsDeleting(false)
      setDeleteProgress(0)
    }
  }
  const truncateFileName = (
    fileName: string,
    maxLength: number = 50,
  ): string => {
    const cleanedName = cleanFileName(fileName)
    if (cleanedName.length <= maxLength) return cleanedName

    const extension = cleanedName.slice(cleanedName.lastIndexOf('.'))
    const nameWithoutExt = cleanedName.slice(0, cleanedName.lastIndexOf('.'))

    return (
      nameWithoutExt.slice(0, maxLength - extension.length - 3) +
      '...' +
      extension
    )
  }

  const handleRemoveFromAlbum = async (fileNames: string[]) => {
    try {
      await Promise.all(
        fileNames.map(async fileName => {
          const image = images.find(img => img.fileName === fileName)
          if (!image) return

          const newStatus = removeFromAlbum(image.status, selectedAlbum)
          await handleUpdateStatus(fileName, newStatus)
        }),
      )

      toast.success(t('images-removed-from-album'))
      setAlbumGallerySelection([])
      await loadImages()
    } catch (error) {
      console.error('Failed to remove images from album:', error)
      toast.error(t('failed-to-remove-images-from-album'))
    }
  }

  return (
    <div className=''>
      <div className='space-y-2'>
        <div className='w-full rounded-lg flex flex-col h-full overflow-hidden'>
          <div className='shrink-0'>
            <GalleryUsageInfo
              images={images}
              onStorageCheck={checkStorageAvailability}
            />
            {images.filter(
              image => !isImageInAlbum(image.status, selectedAlbum),
            ).length > 0 && (
              <div className='flex items-center gap-2 p-2 bg-white rounded-lg mb-1'>
                <FaInfoCircle className='text-redpink w-5 h-5 shrink-0' />
                <p className='text-sm text-anthracite'>
                  {t('main-gallery-info')}
                </p>
              </div>
            )}
          </div>

          <div
            className='flex-1 overflow-y-auto min-h-0 
 scrollbar-thin scrollbar-thumb-anthracite/50 hover:scrollbar-thumb-anthracite 
 scrollbar-track-transparent 
 [&::-webkit-scrollbar]:w-2 
 [&::-webkit-scrollbar-track]:bg-transparent 
 [&::-webkit-scrollbar-thumb]:rounded-full'
          >
            <div
              className='grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-8
             gap-1'
            >
              <div className='aspect-square'>
                <UploadZone
                  onImagesUploaded={handleImagesUploaded}
                  onStorageCheck={checkStorageAvailability}
                />
              </div>
              <GalleryContainer
                images={images.filter(
                  image =>
                    !isImageInAlbum(image.status, selectedAlbum) &&
                    !image.fileName.startsWith('(verification-'),
                )}
                selectedImages={mainGallerySelection}
                onImageSelect={handleMainGallerySelection}
                onImagesChange={loadImages}
                selectedAlbum={selectedAlbum}
              />
            </div>
          </div>
          {images.filter(image => !isImageInAlbum(image.status, selectedAlbum))
            .length > 0 && (
            <div className='shrink-0 mt-1'>
              <GalleryActionButtons
                onSelectAll={() => handleSelectAll('main')}
                onDeselectAll={() => setMainGallerySelection([])}
                onDelete={() => setIsDeleteModalOpen(true)}
                onAddToAlbum={() => handleAddToAlbum(mainGallerySelection)}
                selectedCount={mainGallerySelection.length}
                t={t}
                albumType={selectedAlbum}
              />
            </div>
          )}
        </div>

        <div className='w-full rounded-lg flex flex-col h-full'>
          <div className='shrink-0 mb-1'>
            <div className='w-full bg-anthracite text-white rounded-lg'>
              <AlbumSelect
                value={selectedAlbum}
                onChange={handleAlbumChange}
                t={t}
                displayMode={isGalleryPage ? 'select' : 'text'}
              />
            </div>
          </div>

          <div className=''>
            <GalleryAlbums
              images={images.filter(image =>
                isImageInAlbum(image.status, selectedAlbum),
              )}
              selectedImages={albumGallerySelection}
              onImageSelect={handleAlbumGallerySelection}
              onImagesChange={loadImages}
              albumType={selectedAlbum}
              onUpdateStatus={handleUpdateStatus}
            />
          </div>
          {images.filter(image => isImageInAlbum(image.status, selectedAlbum))
            .length > 0 && (
            <div className='mt-1'>
              <GalleryActionButtons
                onSelectAll={() => handleSelectAll('album')}
                onDeselectAll={() => setAlbumGallerySelection([])}
                onRemoveFromAlbum={() =>
                  handleRemoveFromAlbum(albumGallerySelection)
                }
                selectedCount={albumGallerySelection.length}
                t={t}
                isAlbumView
              />
            </div>
          )}
        </div>
      </div>

      {isDeleteModalOpen && (
        <div className='fixed inset-0 bg-black/30 flex items-center justify-center p-4 z-50'>
          <div className='bg-white rounded-lg p-6 max-w-xl'>
            <div className='flex flex-col items-center text-center'>
              <div className='relative mb-4'>
                <Trash2 className='w-16 h-16 text-redpink' />
              </div>

              <h3 className='text-xl font-bold text-gray-900 mb-2'>
                {t('confirm-delete-multiple')}
              </h3>

              <p className='text-gray-600 mb-4'>
                {t('delete-multiple-warning', {
                  count: mainGallerySelection.length,
                })}
              </p>

              {mainGallerySelection.some(fileName => {
                const image = images.find(img => img.fileName === fileName)
                return image && image.status !== 0
              }) && (
                <div className='w-full bg-redlight/80 p-4 rounded-lg mb-6'>
                  <p className='text-sm font-medium text-gray-700 mb-2'>
                    {t('images-present-in-albums')}:
                  </p>

                  <div className='space-y-2 text-left'>
                    {mainGallerySelection.map(fileName => {
                      const image = images.find(
                        img => img.fileName === fileName,
                      )
                      if (!image || image.status === 0) return null

                      const albumNames = []
                      if (isImageInAlbum(image.status, 'announce')) {
                        albumNames.push(`${t('create-ad.announce')}`)
                      }
                      if (isImageInAlbum(image.status, 'event')) {
                        albumNames.push(`${t('event')}`)
                      }
                      if (isImageInAlbum(image.status, 'jobOffer')) {
                        albumNames.push(`${t('album-jobOffer')}`)
                      }

                      return albumNames.length ? (
                        <div
                          key={fileName}
                          className='flex items-center gap-2 text-sm text-redpink'
                        >
                          <div className='w-1.5 h-1.5 rounded-full bg-redpink shrink-0'></div>
                          <span>
                            {truncateFileName(fileName)} :{' '}
                            {albumNames.join(', ')}
                          </span>
                        </div>
                      ) : null
                    })}
                  </div>
                </div>
              )}

              <div className='flex flex-col gap-4 w-full'>
                {isDeleting && (
                  <div className='w-full'>
                    <div className='flex justify-between text-sm text-gray-600 mb-2'>
                      <span>{t('deleting-images')}</span>
                      <span>{Math.round(deleteProgress)}%</span>
                    </div>
                    <div className='w-full bg-gray-200 rounded-full h-2'>
                      <div
                        className='h-2 rounded-full bg-redpink transition-all duration-300'
                        style={{ width: `${deleteProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className='flex gap-2 justify-end'>
                  <MyButton
                    variant='anthracite'
                    onClick={() => setIsDeleteModalOpen(false)}
                    className='text-sm px-6 py-2'
                    disabled={isDeleting}
                  >
                    {t('cancel')}
                  </MyButton>
                  <MyButton
                    variant='redpink'
                    onClick={handleDeleteSelected}
                    className='text-sm px-6 py-2'
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <div className='flex items-center gap-2'>
                        <span className='animate-pulse'>{t('deleting')}</span>
                      </div>
                    ) : (
                      t('delete')
                    )}
                  </MyButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <StorageFullModal
        isOpen={showStorageFullModal}
        onClose={() => setShowStorageFullModal(false)}
        totalSize={totalSize}
        maxSize={maxSize}
        remainingSize={maxSize - totalSize}
      />
    </div>
  )
}

export default GalleryMegaEdit
